import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toMillis } from '../utils/helpers';

// Single list card wrapped as a sortable item. Translate3d for smooth drag
// tracking; cursor flips to grabbing while pressed.
const SortableListCard = ({ list, items, listShares, onSelect, getListIcon, getListIconColor, getListSubtitle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: list.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    boxShadow: isDragging ? '0 12px 24px rgba(0,0,0,0.18)' : undefined,
    opacity: isDragging ? 0.92 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="list-card"
      onClick={() => { if (!isDragging) onSelect(list); }}
    >
      <div className="list-card-header">
        <div className={`list-card-icon ${getListIconColor(list.name)}`}>
          {getListIcon(list.name)}
        </div>
        <span className="list-card-count">
          {(items[list.id] || []).filter(item => !item.is_completed).length}
        </span>
      </div>
      <h3 className="list-card-title">{list.name}</h3>
      <p className="list-card-subtitle">{getListSubtitle(list)}</p>
    </div>
  );
};

const formatTimeSince = (timestamp) => {
  if (!timestamp) return '';
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const Dashboard = ({ lists, items, onCreateList, onDeleteList, onReorderLists, onSelectList, listShares, onManageAccess, onShowAccountSettings, user, isOnline, lastOnlineAt, pendingCount }) => {
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newListName, setNewListName] = useState('');

  // Stable display order — sort by `position` (user-controlled). Lists without
  // `position` (older data) fall back to `created_at` so they still order
  // sensibly. We do NOT sort by `updated_at` here; that would re-shuffle the
  // dashboard every time an item inside any list changes.
  const sortedLists = useMemo(() => {
    return [...(lists || [])].sort((a, b) => {
      const getPos = (l) => (typeof l.position === 'number' ? l.position : toMillis(l.created_at || 0));
      return getPos(b) - getPos(a);
    });
  }, [lists]);

  // Sensors: require a small movement before drag starts so taps still
  // navigate. Distance of 5px is enough to feel responsive without
  // accidentally triggering on click.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortedLists.findIndex(l => l.id === active.id);
    const newIndex = sortedLists.findIndex(l => l.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(sortedLists, oldIndex, newIndex);
    if (onReorderLists) onReorderLists(next);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  // Get icon color based on list name
  const getListIconColor = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('grocer') || lowerName.includes('food') || lowerName.includes('shop')) {
      return 'orange';
    }
    if (lowerName.includes('costco') || lowerName.includes('bulk') || lowerName.includes('warehouse')) {
      return 'purple';
    }
    return 'blue';
  };

  // Get icon for list
  const getListIcon = (name) => {
    const color = getListIconColor(name);
    if (color === 'orange') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    }
    if (color === 'purple') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    );
  };

  // Get subtitle for list
  const getListSubtitle = (list) => {
    if (!list.isOwner && list.shareInfo) {
      return `Shared by ${list.shareInfo.sharedByName || 'someone'}`;
    }
    const shares = listShares[list.id] || [];
    const acceptedShares = shares.filter(s => s.status === 'accepted');
    if (acceptedShares.length > 0) {
      return `Shared w/ ${acceptedShares[0].email.split('@')[0]}`;
    }
    if (list.visibility === 'public') {
      return 'Public list';
    }
    return 'Personal list';
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;

    // Create a fake event that matches the expected format in useListOperations
    const fakeEvent = {
      preventDefault: () => {},
      target: {
        elements: {
          newListName: { value: newListName }
        },
        reset: () => {} // Hook calls e.target.reset() to clear the form
      }
    };
    onCreateList(fakeEvent);
    setNewListName('');
    setShowCreateInput(false);
  };

  return (
    <div className="dashboard-new">
      {/* Header */}
      <div className="dashboard-header">
        <h2>My Lists</h2>
        <div
          className="user-avatar-badge"
          onClick={onShowAccountSettings}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && onShowAccountSettings()}
        >
          {getUserInitials()}
        </div>
      </div>

      <div className="dashboard-content">
        {/* Sync Status Banner */}
        <div className={`sync-status-banner ${!isOnline ? 'sync-offline' : pendingCount > 0 ? 'sync-pending' : ''}`}>
          <div className="sync-status-dot"></div>
          <p className="sync-status-text">
            {!isOnline
              ? `Offline${lastOnlineAt ? ` - Last synced ${formatTimeSince(lastOnlineAt)}` : ''}`
              : pendingCount > 0
                ? `Syncing ${pendingCount} change${pendingCount !== 1 ? 's' : ''}...`
                : 'Live Sync Active'}
          </p>
        </div>

        {/* Sortable card grid — @dnd-kit handles 2D grid drag-and-drop.
            On drop, the array is reordered and we persist new positions. */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortedLists.map(l => l.id)} strategy={rectSortingStrategy}>
            <div className="card-grid">
              {sortedLists.map(list => (
                <SortableListCard
                  key={list.id}
                  list={list}
                  items={items}
                  listShares={listShares}
                  onSelect={onSelectList}
                  getListIcon={getListIcon}
                  getListIconColor={getListIconColor}
                  getListSubtitle={getListSubtitle}
                />
              ))}
              {/* Create New Card lives in the same grid as the sortable cards
                  so it naturally occupies the next free cell. It's not
                  registered with SortableContext, so it won't drag. */}
              {showCreateInput ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="list-card create-card"
                >
                  <form onSubmit={handleCreateSubmit}>
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="List name..."
                      className="create-list-input"
                      autoFocus
                      onBlur={() => {
                        if (!newListName.trim()) {
                          setShowCreateInput(false);
                        }
                      }}
                    />
                    <div className="create-card-actions">
                      <button type="button" onClick={() => setShowCreateInput(false)} className="create-card-cancel">
                        Cancel
                      </button>
                      <button type="submit" className="create-card-submit">
                        Create
                      </button>
                    </div>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="list-card create-card"
                  onClick={() => setShowCreateInput(true)}
                >
                  <div className="list-card-header">
                    <div className="list-card-icon blue">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="list-card-title">Create New</h3>
                  <p className="list-card-subtitle">AI Generated</p>
                </motion.div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default Dashboard;
